// IndexedDB storage for inspection report media drafts.
//
// Why IndexedDB instead of localStorage? Photos are binary blobs — localStorage
// only holds strings. IndexedDB can store Blob/File objects natively.
//
// Storage layout:
//   DB: inspectionMediaDraft  (one per browser origin)
//   Store: mediaBlobs         (one record per file)
//   Key:   jobId::section::systemId::filename
//
// Sections with system rows (hvac, plumbing) use the system's UUID as systemId.
// Flat sections (electrical, waterTreatment, airQuality) use FLAT_SENTINEL ('_')
// because they have no per-system identity.
//
// Lifecycle:
//   - Files are saved slot-by-slot as the user uploads them.
//   - On form mount, if a text draft exists (localStorage), blobs are restored.
//   - If no text draft exists, blobs for that job are deleted (orphan cleanup).
//   - On submit, all blobs for the job are deleted after a successful API call.

const DB_NAME = 'inspectionMediaDraft';
const DB_VERSION = 1;
const STORE_NAME = 'mediaBlobs';

/**
 * Maximum total bytes allowed per save call (incoming files only).
 * Lower this value to test the quota warning with small files.
 * Default: 50 MB
 */
export const MAX_MEDIA_DRAFT_BYTES = 50 * 1024 * 1024;

interface MediaDraftRecord {
  key: string; // `${jobId}::${section}::${systemId}::${filename}`
  jobId: string;
  section: string;
  systemId: string;
  filename: string;
  mimeType: string;
  size: number;
  blob: Blob;
  savedAt: string;
}

// Module-level promise cache so every call shares the same connection.
// Reset to null on db.onclose so a dropped connection is transparently reopened.
let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        // by_job: used to delete all blobs for a job at once (submit / orphan cleanup)
        store.createIndex('by_job', 'jobId', { unique: false });
        // by_slot: used to replace all files in a slot without touching other slots
        store.createIndex('by_slot', ['jobId', 'section', 'systemId'], { unique: false });
      }
    };

    request.onblocked = () => {
      console.warn('IndexedDB upgrade blocked — close other tabs using this app');
    };

    request.onsuccess = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      db.onclose = () => {
        dbPromise = null;
      };
      resolve(db);
    };

    request.onerror = (e) => {
      dbPromise = null;
      reject((e.target as IDBOpenDBRequest).error);
    };
  });

  return dbPromise;
}

function slotKey(jobId: string, section: string, systemId: string, filename: string): string {
  return `${jobId}::${section}::${systemId}::${filename}`;
}

/** Save all files for a slot. Replaces any previously stored files for the same slot. */
export async function saveMediaSlot(jobId: string, section: string, systemId: string, files: File[]): Promise<void> {
  // App-level size guard — throws QuotaExceededError before hitting the browser limit.
  // isQuotaError() in useMediaDraft handles both this and the native browser error.
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  if (totalBytes > MAX_MEDIA_DRAFT_BYTES) {
    throw new DOMException(
      `Draft media exceeds the ${Math.round(MAX_MEDIA_DRAFT_BYTES / 1024 / 1024)} MB limit`,
      'QuotaExceededError',
    );
  }

  const db = await openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);

    // Delete-then-write: cursor over the compound index to remove stale records
    // before writing new ones. This handles filename changes and file count changes.
    const slotIndex = store.index('by_slot');
    const range = IDBKeyRange.only([jobId, section, systemId]);
    const deleteRequest = slotIndex.openCursor(range);

    deleteRequest.onsuccess = (e) => {
      const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        // All old records deleted — now write new ones
        const now = new Date().toISOString();
        for (const file of files) {
          const record: MediaDraftRecord = {
            key: slotKey(jobId, section, systemId, file.name),
            jobId,
            section,
            systemId,
            filename: file.name,
            mimeType: file.type,
            size: file.size,
            blob: file,
            savedAt: now,
          };
          store.put(record);
        }
      }
    };

    tx.oncomplete = () => resolve();
    tx.onerror = (e) => reject((e.target as IDBTransaction).error);
    // onabort fires when the browser aborts the transaction (e.g. storage pressure).
    // Without this handler the promise would hang forever.
    tx.onabort = () => reject(new Error('saveMediaSlot: transaction aborted'));
  });
}

/** Load all files for a slot. Returns an empty array if the slot has no stored records. */
export async function loadMediaSlot(jobId: string, section: string, systemId: string): Promise<File[]> {
  const db = await openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const slotIndex = store.index('by_slot');
    const range = IDBKeyRange.only([jobId, section, systemId]);
    const request = slotIndex.getAll(range);

    request.onsuccess = (e) => {
      const records = (e.target as IDBRequest<MediaDraftRecord[]>).result;
      const files = records.map((r) => new File([r.blob], r.filename, { type: r.mimeType }));
      resolve(files);
    };

    request.onerror = (e) => reject((e.target as IDBRequest).error);
  });
}

/** Remove all stored media for a given job. Used on submit and orphan cleanup. */
export async function deleteMediaForJob(jobId: string): Promise<void> {
  const db = await openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const jobIndex = store.index('by_job');
    const range = IDBKeyRange.only(jobId);
    const request = jobIndex.openCursor(range);

    request.onsuccess = (e) => {
      const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    tx.oncomplete = () => resolve();
    tx.onerror = (e) => reject((e.target as IDBTransaction).error);
    tx.onabort = () => reject(new Error('deleteMediaForJob: transaction aborted'));
  });
}

/** Return the distinct jobIds present in the store. */
export async function listJobIds(): Promise<string[]> {
  const db = await openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const index = store.index('by_job');
    const request = index.openKeyCursor(null, 'nextunique');
    const ids: string[] = [];

    request.onsuccess = (e) => {
      const cursor = (e.target as IDBRequest<IDBCursor>).result;
      if (cursor) {
        ids.push(cursor.key as string);
        cursor.continue();
      }
    };

    tx.oncomplete = () => resolve(ids);
    tx.onerror = (e) => reject((e.target as IDBTransaction).error);
  });
}
